import { createAuthClient } from 'better-auth/react'
import { passkeyClient } from '@better-auth/passkey/client'
import { anonymousClient } from 'better-auth/client/plugins'
import { startAuthentication } from '@simplewebauthn/browser'

export const authClient = createAuthClient({
  baseURL: typeof window !== 'undefined'
    ? `${window.location.origin}/api/auth`
    : 'http://localhost:3001/api/auth',
  plugins: [passkeyClient(), anonymousClient()],
})

// Convenience exports
export const {
  useSession,
  signIn,
  signUp,
  signOut,
} = authClient

/** Trigger passkey registration (add a passkey to the current account) */
export const addPasskey = (opts?: { name?: string }) =>
  authClient.passkey.addPasskey(opts)

/** Create an anonymous Better Auth session (used for passkey-first signup) */
export const signInAnonymously = () => authClient.signIn.anonymous()

type AuthErrorShape = {
  code?: string
  message: string
  status: number
  statusText: string
}

type AuthResult<T = unknown> = {
  data: T | null
  error: AuthErrorShape | null
}

const AUTH_BASE = '/api/auth'
const PASSKEY_OPTION_KEYS = ['data', 'options', 'publicKey', 'optionsJSON', 'result'] as const
const PASSKEY_AUTHENTICATE_OPTIONS_PATH = '/passkey/generate-authenticate-options'

async function parseResponseBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function parseApiError(payload: unknown, fallbackStatus: number, fallbackStatusText: string): AuthErrorShape {
  if (typeof payload === 'string' && payload.trim().length > 0) {
    return {
      message: payload,
      status: fallbackStatus,
      statusText: fallbackStatusText,
    }
  }

  if (payload && typeof payload === 'object') {
    const data = payload as {
      code?: string
      message?: string
      error?: { code?: string; message?: string }
    }
    return {
      code: data.code ?? data.error?.code,
      message: data.message ?? data.error?.message ?? fallbackStatusText,
      status: fallbackStatus,
      statusText: fallbackStatusText,
    }
  }

  return {
    message: fallbackStatusText,
    status: fallbackStatus,
    statusText: fallbackStatusText,
  }
}

function topLevelKeys(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return []
  return Object.keys(payload as Record<string, unknown>).slice(0, 20)
}

function logPasskeyOptionsDiagnostics(
  reason: string,
  res: Response,
  payload: unknown,
): void {
  const contentType = res.headers.get('content-type')
  const payloadType = Array.isArray(payload) ? 'array' : typeof payload
  const keys = topLevelKeys(payload)

  console.error('[auth.passkey] option-shape-diagnostic', {
    reason,
    status: res.status,
    redirected: res.redirected,
    responsePath: responsePathFromUrl(res.url),
    contentType,
    payloadType,
    topLevelKeys: keys,
  })
}

function responsePathFromUrl(url: string): string | null {
  if (!url) return null
  try {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const parsed = new URL(url, fallbackOrigin)
    return parsed.pathname
  } catch {
    return null
  }
}

function isLikelyHtmlPayload(res: Response, payload: unknown): boolean {
  const contentType = res.headers.get('content-type')?.toLowerCase() ?? ''
  if (contentType.includes('text/html')) return true
  if (typeof payload !== 'string') return false
  const sample = payload.trimStart().slice(0, 32).toLowerCase()
  return sample.startsWith('<!doctype html') || sample.startsWith('<html')
}

async function fetchAuthenticateOptions(cacheBust = false): Promise<{ response: Response; body: unknown }> {
  const url = cacheBust
    ? `${AUTH_BASE}${PASSKEY_AUTHENTICATE_OPTIONS_PATH}?_ts=${Date.now()}`
    : `${AUTH_BASE}${PASSKEY_AUTHENTICATE_OPTIONS_PATH}`

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })

  const body = await parseResponseBody(response)
  return { response, body }
}

function findPasskeyOptions(value: unknown, depth = 0): Record<string, unknown> | null {
  if (depth > 6 || value == null) return null

  if (typeof value === 'string') {
    try {
      return findPasskeyOptions(JSON.parse(value), depth + 1)
    } catch {
      return null
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findPasskeyOptions(item, depth + 1)
      if (found) return found
    }
    return null
  }

  if (typeof value !== 'object') return null
  const obj = value as Record<string, unknown>
  if (typeof obj.challenge === 'string' && obj.challenge.length > 0) return obj

  for (const key of PASSKEY_OPTION_KEYS) {
    const found = findPasskeyOptions(obj[key], depth + 1)
    if (found) return found
  }

  for (const nested of Object.values(obj)) {
    const found = findPasskeyOptions(nested, depth + 1)
    if (found) return found
  }

  return null
}

function normalizeAuthenticateOptions(payload: unknown): Record<string, unknown> | null {
  const unwrapped = findPasskeyOptions(payload)
  if (!unwrapped) return null

  const options = { ...unwrapped } as Record<string, unknown>
  if (Array.isArray(options.allowCredentials)) {
    // Filter malformed credentials from legacy rows to avoid browser-side WebAuthn crashes.
    const validCredentials = options.allowCredentials.filter((cred) => {
      if (!cred || typeof cred !== 'object') return false
      const id = (cred as Record<string, unknown>).id
      return typeof id === 'string' && id.length > 0
    })
    if (validCredentials.length > 0) {
      options.allowCredentials = validCredentials
    } else {
      delete options.allowCredentials
    }
  }

  return options
}

/** Sign in using a stored passkey (primary auth method). */
export const signInWithPasskey = async (): Promise<AuthResult> => {
  let { response: optionsRes, body: optionsBody } = await fetchAuthenticateOptions()

  if (isLikelyHtmlPayload(optionsRes, optionsBody)) {
    logPasskeyOptionsDiagnostics('html_response_retrying', optionsRes, optionsBody)
    const retry = await fetchAuthenticateOptions(true)
    optionsRes = retry.response
    optionsBody = retry.body
  }

  if (!optionsRes.ok) {
    logPasskeyOptionsDiagnostics('http_error', optionsRes, optionsBody)
    return {
      data: null,
      error: parseApiError(optionsBody, optionsRes.status, optionsRes.statusText),
    }
  }

  const optionsJSON = normalizeAuthenticateOptions(optionsBody)
  if (!optionsJSON) {
    logPasskeyOptionsDiagnostics('invalid_options_shape', optionsRes, optionsBody)
    return {
      data: null,
      error: {
        code: 'INVALID_PASSKEY_OPTIONS',
        message: 'Invalid passkey options returned by server',
        status: 500,
        statusText: 'INTERNAL_SERVER_ERROR',
      },
    }
  }

  try {
    const authResponse = await startAuthentication({ optionsJSON: optionsJSON as any })
    const verifyRes = await fetch(`${AUTH_BASE}/passkey/verify-authentication`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response: authResponse }),
    })
    const verifyBody = await parseResponseBody(verifyRes)

    if (!verifyRes.ok) {
      return {
        data: null,
        error: parseApiError(verifyBody, verifyRes.status, verifyRes.statusText),
      }
    }

    return { data: verifyBody, error: null }
  } catch (err) {
    return {
      data: null,
      error: {
        code: 'AUTH_CANCELLED',
        message: err instanceof Error ? err.message : 'Passkey authentication cancelled',
        status: 400,
        statusText: 'BAD_REQUEST',
      },
    }
  }
}

/** Sign in with email + password (fallback) */
export const signInWithEmail = (email: string, password: string) =>
  authClient.signIn.email({ email, password })

/** Register a new account with email + password */
export const signUpWithEmail = (name: string, email: string, password: string) =>
  authClient.signUp.email({ name, email, password })

/** Request changing the current account email (sends verification email if configured) */
export const requestEmailVerification = (newEmail: string, callbackURL?: string) =>
  authClient.changeEmail({ newEmail, callbackURL })
